import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { useLayoutVariant } from '../../hooks/useLayoutVariant'

export default function Modal({ isOpen, onClose, children, size = 'md' }) {
  const { isAdminAppUI } = useLayoutVariant()
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-7xl'
  }

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={clsx(
            'fixed inset-0 transition-opacity',
            isAdminAppUI ? 'bg-black/60' : 'bg-slate-900/40 backdrop-blur-sm'
          )} />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-3 sm:p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className={clsx(
                'relative transform overflow-hidden px-4 pb-4 pt-5 text-left transition-all w-full mx-2 sm:mx-4 sm:my-8 sm:w-full sm:p-6 max-h-[90vh] overflow-y-auto',
                sizeClasses[size],
                isAdminAppUI
                  ? 'bg-[#1a2535] shadow-xl ring-1 ring-[#243246] rounded-[14px]'
                  : 'bg-white shadow-strong ring-1 ring-pastel-pink/80 backdrop-blur-sm bg-white/95 rounded-3xl'
              )}>
                {/* Efecto de brillo sutil */}
                <div className={clsx(
                  'absolute top-0 left-0 right-0 h-1',
                  isAdminAppUI
                    ? 'bg-[#00c896] opacity-80'
                    : 'bg-gradient-to-r from-pastel-teal via-primary-400 to-pastel-teal opacity-60'
                )} />
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export function ModalHeader({ children, onClose, className = '' }) {
  const { isAdminAppUI } = useLayoutVariant()
  return (
    <div className={clsx('flex items-center justify-between mb-4 sm:mb-6', className)}>
      <h3 className={clsx(
        'text-lg sm:text-xl font-semibold leading-6 pr-2',
        isAdminAppUI ? 'text-[#e8edf5]' : 'text-pastel-ink'
      )}>
        {children}
      </h3>
      {onClose && (
        <button
          onClick={onClose}
          className={clsx(
            'p-1.5 focus:outline-none focus:ring-2 transition-colors flex-shrink-0',
            isAdminAppUI
              ? 'rounded-[10px] text-[#7a8fa6] hover:text-[#e8edf5] hover:bg-[#1f2d40] focus:ring-[#00c896]/30'
              : 'rounded-2xl text-gray-400 hover:text-gray-500 hover:bg-pastel-cream focus:ring-pastel-teal'
          )}
          aria-label="Cerrar"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  )
}

export function ModalContent({ children, className = '' }) {
  const { isAdminAppUI } = useLayoutVariant()
  return (
    <div className={clsx(isAdminAppUI ? 'text-[#e8edf5]' : 'text-slate-700', className)}>
      {children}
    </div>
  )
}

export function ModalFooter({ children, className = '' }) {
  const { isAdminAppUI } = useLayoutVariant()
  return (
    <div className={clsx(
      'mt-4 sm:mt-6 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 border-t pt-4',
      isAdminAppUI ? 'border-[#243246]' : 'border-pastel-pink',
      className
    )}>
      {children}
    </div>
  )
}
